import { Component, ElementRef, ViewChild, OnInit, ViewChildren, QueryList, OnDestroy, AfterViewChecked, inject } from '@angular/core';
import { NgxImageCompressService } from 'ngx-image-compress';
import { Router, RouterModule } from '@angular/router';
import { CommonModule, Location, SlicePipe } from '@angular/common';
import { FormsModule, Validators, ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import dayjs from 'dayjs';
import { filter, Subject, takeUntil } from 'rxjs';
import { Dialog } from '@angular/cdk/dialog';

import { AuthService } from '../../auth.service';
import { PhotoService } from '../../photo/photo.service';
import { HomeService } from '../../home/home.service';
import { MySpaceService } from '../my-space.service';
import { LoaderService } from '../../loader.service';
import { LayoutService } from '../../layout.service';

import { LoaderComponent } from '../../shared/loader/loader.component';
import { BirthdateInputComponent } from '../../shared/birthdate-input/birthdate-input.component';
import { CityInputComponent } from '../../shared/city-input/city-input.component';
import { ErrorDialogComponent } from '../../dialog/error-dialog/error-dialog.component';
import { TopButtonComponent } from '../../shared/top-button/top-button.component';
import { GoBackButtonComponent } from '../../shared/go-back-button/go-back-button.component';
import { ProfilePhotosDialogComponent } from '../../dialog/profile-photos-dialog/profile-photos-dialog.component';

import { ClickAnywhereDirective } from '../../click-anywhere.directive';

import { User } from '../../shared/interfaces/user';
import { City } from '../../shared/interfaces/city';
import { Photo } from '../../shared/interfaces/photo';
import { firstnameValidator } from '../../shared/validators/firstnameValidator';
import { Hobby } from '../../shared/interfaces/hobby';
import { dateValidator } from '../../shared/validators/dateValidator';

@Component({
  selector: 'app-my-profile',
  standalone: true,
  imports: [
    LoaderComponent, 
    FormsModule, 
    ReactiveFormsModule, 
    CityInputComponent, 
    BirthdateInputComponent,
    RouterModule,
    SlicePipe,
    CommonModule,
    ErrorDialogComponent,
    ClickAnywhereDirective,
    TopButtonComponent,
    GoBackButtonComponent
  ],
  templateUrl: './my-profile.component.html',
  styleUrl: './my-profile.component.scss'
})
export class MyProfileComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('fileUpload') fileUpload!: ElementRef
  @ViewChildren('contextualMenuClosed') contextualMenusClosed!: QueryList<ElementRef>
  @ViewChildren('contextualMenuOpened') contextualMenusOpened!: QueryList<ElementRef>
  @ViewChild('cropper') cropper!: ElementRef
  @ViewChild('top') top!: ElementRef
  @ViewChild('cropperContainer') cropperContainer!: ElementRef
  @ViewChild('hobbiesListContainer') hobbiesListContainer!: ElementRef
  
  loading$ = this.loaderService.loading$
  deleting = false
  me!: User
  user!: User
  photos: (Photo | null)[] = []
  fileList: FileList | null = null
  tempCropperPhotoInBase64!: string
  showCropper!: { 
    data: File,
    temp?: string,
    size: { height: number; width: number; }
  } | null
  croppedImage: string = ''
  cropData!: { x: number; y: number; height: number; width: number; preview: string; }
  editInformationMode = false
  editProfileInformationFormGroup!: FormGroup
  displayCreatingLoader = false
  userAge!: number
  newPhotoIndex!: number
  //selectedAttraction = 'woman'
  selectedCity: City | null = null

  day = ''
  month = ''
  year = ''

  firstnameError = false
  dateError = false
  errorMessage: string | null = null
  errorTitle: string | null = null
  photoContainerDimensions!: {width: number, height: number}
  hobbyWidth!: number
  userHobbies: Hobby[] = []
  dialog = inject(Dialog);
  photoToDiscardId!: number
  updateMainPhotoError: {title: string, message: string} | null = null
  private readonly _destroyed = new Subject<void>()
  isDesktop = this.layoutService.isDesktop
  formerMainPhoto: Photo | null = null
  descriptionMaxLength = 500

  constructor(
    private authService: AuthService,
    private imageCompress: NgxImageCompressService,
    private photoService: PhotoService,
    private router: Router,
    private location: Location,
    private homeService: HomeService,
    private mySpaceService: MySpaceService,
    private loaderService: LoaderService,
    private layoutService: LayoutService
  ){}

  ngOnInit(){
    this.loaderService.show()
    
    // define default value for the user for DOM build
    this.user = this.authService.get('user') ? JSON.parse(this.authService.get('user')) : null
    this.authService.getProfile(parseInt(this.authService.get('user_id'))).pipe(takeUntil(this._destroyed)).subscribe(res => {
      this.user = res

      if(!this.user.main_photo) this.router.navigate(['/upload-photo'])

      for (let i = 0; i < 6; i++) {
        if(!this.user.photos?.[i]){
          this.photos.push(null)
        } else {
          this.photos.push(this.user.photos[i])
        }
      }

      if(this.user.birthdate){
        const [y,m,d] = this.user.birthdate.split('-')
        const date = `${y}-${m}-${d}`
        const birthDate = dayjs(date)
  
        this.userAge = dayjs().diff(birthDate, 'year')
      }

      this.loaderService.hide()

      if(!this.user.birthdate) return
      this.day = this.user.birthdate.substring(8)
      this.month = this.user.birthdate.substring(5,7)
      this.year = this.user.birthdate.substring(0,4)

      this.editProfileInformationFormGroup = new FormGroup({
        firstnameCtrl: new FormControl(this.user.firstname || '', [Validators.required, Validators.minLength(2), Validators.maxLength(25), firstnameValidator()]),
        birthdayDateCtrl: new FormGroup({
          birthDayCtrl: new FormControl(this.day || '', [Validators.required]),
          birthMonthCtrl: new FormControl(this.month || '', [Validators.required]),
          birthYearCtrl: new FormControl(this.year || '', [Validators.required]),
        }, {validators: dateValidator()}),
        locationCtrl: new FormGroup({
          cityCtrl: new FormControl(this.user.city?.id, [Validators.required])
        }),
        //attractionCtrl: new FormControl(this.user.attraction || this.user.sex === 'man' ? Gender.woman : Gender.man || '', [Validators.required]),
        descriptionCtrl: new FormControl(this.user.description || '', [Validators.required])
      })

      this.editProfileInformationFormGroup.get('firstnameCtrl')?.statusChanges.pipe(takeUntil(this._destroyed)).subscribe(() => {
        this.firstnameError = false
  
        /*  if(!this.editProfileInformationFormGroup.get('firstnameCtrl')?.errors) {
          console.log('pas d erreur')
        } */
  
        if(this.editProfileInformationFormGroup.get('firstnameCtrl')?.errors){
          this.errorTitle = $localize `:title for the firstname input error message in the profile edition form@@INVALID_FIRSTNAME:Invalid first name`
          this.errorMessage = $localize `:Description for the firstname input error message in the profile edition form@@ERROR_WRONG_FIRSTNAME:ERROR. Your first name must be between 2 and 25 characters and must not contain any special characters.`
          this.firstnameError = true
        }
      })

      this.mySpaceService.getHobbiesByUser(this.user.id).pipe(takeUntil(this._destroyed)).subscribe(res => this.userHobbies = res)

      this.editProfileInformationFormGroup.get('birthdayDateCtrl')?.statusChanges.subscribe(() => {
        if(!this.editProfileInformationFormGroup.get('birthdayDateCtrl')?.errors) {
          //console.log('pas d erreur')
          this.dateError = false
        }

        if(this.editProfileInformationFormGroup.get('birthdayDateCtrl')?.errors?.['underAged']){
          this.dateError = true
          this.errorTitle = $localize `:Title of the error dialog when birthdate was less than 18 years ago@@SORRY:Sorry`,
          this.errorMessage = $localize `:Description of the error dialog when birthdate was less than 18 years ago@@UNDERAGE:You must be over 18 years old to subscribe`
        }
        if(this.editProfileInformationFormGroup.get('birthdayDateCtrl')?.errors?.['invalidDate']){
          this.dateError = true
          this.errorTitle = $localize `:Title of the error dialog when birthdate is invalid@@INVALID_DATE:Invalid date`,
          this.errorMessage = $localize `:Description of the error dialog when birthdate is invalid@@DATE_DOES_NOT_EXIST:Given date does not exist. Please check your data and try again.`
        }
      })
    })
  }

  ngAfterViewChecked(){
    if(this.isDesktop() && this.hobbiesListContainer){
      const bounds = this.hobbiesListContainer.nativeElement.getBoundingClientRect()
      const hobbiesContainerWidth = bounds.right - bounds.left

      this.hobbyWidth = hobbiesContainerWidth / 5 - 16
    } else {
      const screenWidth = window.screen.width

      this.hobbyWidth = (screenWidth-20) / 3 - 16
    }
  }

  getStyles() {
    return {
      'width': this.hobbyWidth + 'px',
      'height': this.hobbyWidth + 'px',
    }
  }

  editHobbies(){
    this.router.navigate(['home/my-space/my-profile/my-hobbies'])
  }

  // ngAfterViewInit(){
  //   const bounds = this.cropperContainer.nativeElement.getBoundingClientRect()

  //   console.log(bounds)

  //   this.photoContainerDimensions = {
  //     width: bounds.width,
  //     height: bounds.height
  //   }
  // }

  goBack(){
    if(this.isDesktop()) {
      this.homeService.pageDisplayed.next('')
      this.location.back()
    } else {
      this.homeService.pageDisplayed.next('')
    }
  }

  openFileInput(index: number){
    this.newPhotoIndex = index
    this.fileUpload.nativeElement.click()
  }

  onPhotoInputChange(event: Event){
    console.log('onPhotoInputChange')
    const element = event.currentTarget as HTMLInputElement
    this.fileList = element.files

    if(element.files && element.files.length) {
      const file = element.files[0] // File
      this.photoService.photoFile = file
      const reader = new FileReader()

      reader.onload = () => {
        const img = new Image()
   
        img.onload = () => {
          this.tempCropperPhotoInBase64 = img.src

          this.showCropper = { 
            data: file,
            temp: this.tempCropperPhotoInBase64,
            size: { height: 400, width: 400 }
          }

          this.photoService.photoToCropInBase64 = this.tempCropperPhotoInBase64
          this.router.navigate(['/home/my-space/my-profile/crop'])
        }
       
        img.src = reader.result as string
      }

      reader.readAsDataURL(file)
      
      // reset the event
      element.value = ''
    }
  }

  openProfilePhotosDialog(photo: Photo | undefined, origin: string | null, index?: number) {
    this.mySpaceService.originPhoto = origin

    this.dialog.open(ProfilePhotosDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
      data: photo
    }).closed.pipe(
      filter(res => res !== 'cancel'),
    ).subscribe((res) => {
      if(res === 'main'){

        console.log(photo)
        if(!photo) return

        if(photo.state !== 'validated') {
          this.updateMainPhotoError = {
            title: $localize `:Title for the error message when trying to set a photo not validated as main photo in the profile management page@@NOT_VALIDATED_PHOTO:Photo not validated`, 
            message: $localize `:Description for the error message when trying to set a photo not validated as main photo in the profile management page@@PHOTO_MUST_BE_VALIDATED:This photo has to be monitored by our moderation teams before it can be used as a main photo.`
          }
          return
        }
      
        this.mySpaceService.updateMainPhoto(photo).pipe(takeUntil(this._destroyed)).subscribe({
          next: () => {
            this.formerMainPhoto = this.photos[0]
            const photoIndex = this.photos.indexOf(photo)
  
            // set the new main photo at index 0
            this.photos[0] = (this.photos.splice(photoIndex, 1, photo)[0])
  
            // set the former main_photo at the index of the clicked photo
            this.photos[photoIndex] = this.formerMainPhoto
          }
        })
      } else if(res === 'delete'){
        this.discardPhoto(photo?.id, index)
      }
    })
  }

  discardPhoto(id?: number, index?: number){
    this.loaderService.show()
    if(!id) return

    this.deleting = true
    this.photoToDiscardId = id

    if(index){
      this.contextualMenusClosed.get(index)?.nativeElement.classList.add('show')
      this.contextualMenusOpened.get(index)?.nativeElement.classList.add('hide')
    }

    this.fileList = null
    this.photoService.cropper();
    this.showCropper = null;

    this.photoService.deletePhoto(id).pipe(takeUntil(this._destroyed)).subscribe(res => {
      console.log(res)
      this.user = res
      this.photos = []

      for (let i = 0; i < 6; i++) {
        if(!res.photos?.[i]){
          this.photos.push(null)
        }else{
          this.photos.push(res.photos[i])
        }
      }

      this.loaderService.hide()
      this.deleting = false
    }) 
  }

  // getUserCroppedImage(event: string){
  //   this.croppedImage = event
  // }

  getClickNature(clickNature: string){
    if(clickNature === 'anywhere' && (this.firstnameError === true || this.dateError === true)){
      this.firstnameError = false
      this.dateError = false
    } else if(clickNature === 'anywhere'){
      this.updateMainPhotoError = null
    }
  }

  getScrollRequest(request: string){
    if(request === 'scrollToTop') this.top.nativeElement.scrollIntoView()
  }

  getBirthdateError(error: {errorTitle: string; errorMessage: string;} | null){
    if(error) {
      this.dateError = true
      this.errorTitle = error.errorTitle
      this.errorMessage = error.errorMessage
    }
  }

  getCity(city: City | null){
    if(!city) this.editProfileInformationFormGroup.controls['locationCtrl'].get('cityCtrl')?.patchValue(null)
    else {
      this.editProfileInformationFormGroup.controls['locationCtrl'].get('cityCtrl')?.patchValue(city.name)
      this.selectedCity = city
    }
  }
  
  getCropDetails(event: { x: number; y: number; height: number; width: number; preview: string; }){
    this.cropData = event
  }

  editProfile(){
    this.editInformationMode = true
  }

  onSubmit() {
    console.log('onsubmit my profile')
    this.loaderService.show()
    const data = this.editProfileInformationFormGroup.value
    // birthdate format: 2005-12-08
    const dateString = `${data.birthdayDateCtrl.birthYearCtrl.toString()}-${data.birthdayDateCtrl.birthMonthCtrl}-${data.birthdayDateCtrl.birthDayCtrl}`

    this.authService.setSubscriptionFormData('firstname', data.firstnameCtrl || this.user.firstname)
    this.authService.setSubscriptionFormData('birthdate', dateString)
    this.authService.setSubscriptionFormData('description', data.descriptionCtrl === '' ? '' : data.descriptionCtrl || this.user.description)
    if(this.selectedCity?.id){
      this.authService.setSubscriptionFormData('city_id', this.selectedCity.id.toString())
    } else if(this.user.city?.id){
      this.authService.setSubscriptionFormData('city_id', this.user.city.id.toString())
    } else {
      this.authService.setSubscriptionFormData('city_id', '')
    }

    //update the user
    this.authService.authUpdate().pipe(takeUntil(this._destroyed)).subscribe(res => {
      this.user = res
      this.authService.remove('user')
      this.authService.set('user', JSON.stringify(this.user))
      this.editInformationMode = false
      this.loaderService.hide()

      if(this.user.birthdate){
        const [y,m,d] = this.user.birthdate.split('-')
        const date = `${y}-${m}-${d}`
        const birthDate = dayjs(date)
  
        this.userAge = dayjs().diff(birthDate, 'year')
      }
    })
  }

  getCitySet(city: City | { id: number | null; name: string | null; }){
    if(!city || !city.id) this.editProfileInformationFormGroup.controls['locationCtrl'].setValue(null)
      else this.editProfileInformationFormGroup.controls['locationCtrl'].setValue(city.id)
  }

  cancel($event: Event){
    $event.stopPropagation()
    console.log('cancel')
    this.editInformationMode = false
    
    /* reset the form with origin values */
    this.editProfileInformationFormGroup.controls['firstnameCtrl'].setValue(this.user.firstname || '')
    this.editProfileInformationFormGroup.controls['birthdayDateCtrl'].get('birthDayCtrl')?.setValue(this.day || '')
    this.editProfileInformationFormGroup.controls['birthdayDateCtrl'].get('birthMonthCtrl')?.setValue(this.month || '')
    this.editProfileInformationFormGroup.controls['birthdayDateCtrl'].get('birthYearCtrl')?.setValue(this.year || '')
    this.editProfileInformationFormGroup.controls['locationCtrl'].get('cityCtrl')?.setValue(this.user.city?.id)
    this.editProfileInformationFormGroup.controls['descriptionCtrl'].setValue(this.user.description || '')
  }

  ngOnDestroy(){
    this._destroyed.next()
    this._destroyed.complete()
  }
}
